import { useApolloClient } from "@apollo/client";
import { ApplyItemAction } from "@msys/apply-item-action-isomorphic";
import { ModalActions, ModalContent, ModalDialog } from "@msys/ui";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useUserData } from "../../../auth/useUserData.js";
import {
  WizardSection,
  applyItemActionToApplyItemActionInput,
} from "../WizardSection.js";
import { useQuoteItemCompletionWizardModal_ApplyQuoteItemActionsMutation } from "./QuoteItemCompletionWizardModal.generated";

type HandleClose = () => void;

export function QuoteItemCompletionWizardModal({
  projectId,
  quoteId,
  handleClose,
  itemId,
  refetchAfterApplyItemAction,
}: {
  projectId: string;
  quoteId: string;
  handleClose: HandleClose;
  itemId: string;
  refetchAfterApplyItemAction?: () => Promise<void>;
}) {
  const { t } = useTranslate(["Decisions", "Global"]);

  const viewer = useUserData().currentUser!;

  const client = useApolloClient();
  const [applyQuoteItemActionsMutation] =
    useQuoteItemCompletionWizardModal_ApplyQuoteItemActionsMutation({ client });

  const [isCompleted, setIsCompleted] = React.useState(false);
  const [answers, setAnswers] = React.useState<Array<ApplyItemAction>>([]);

  const handleCloseWithRefetch = React.useCallback(() => {
    client.reFetchObservableQueries();
    refetchAfterApplyItemAction?.();
    handleClose();
  }, [client, handleClose, refetchAfterApplyItemAction]);

  return (
    <ModalDialog maxWidth="lg">
      {isCompleted ? (
        <>
          <ModalContent>
            <Stack
              spacing={1}
              alignItems={"center"}
              justifyContent={"center"}
              height={"100%"}
            >
              <CheckCircleIcon color="success" sx={{ fontSize: 140 }} />
              <Typography variant={"h2"} color="success.main">
                {t("Your answers have been saved!", {
                  ns: "Decisions",
                })}
              </Typography>
              <Typography textAlign={"center"}>
                {t(
                  "Start the wizard again and you will be able to review any question that hasn't been finalised yet.",
                  { ns: "Decisions" }
                )}
              </Typography>
            </Stack>
          </ModalContent>
          <ModalActions
            actionButtons={[
              {
                label: t("Back", { ns: "Global" }),
                handleClick: () => {
                  setIsCompleted(false);
                },
                buttonProps: { variant: "outlined" },
              },
              {
                label: t("Close", { ns: "Global" }),
                handleClick: handleCloseWithRefetch,
                buttonProps: { variant: "contained" },
              },
            ]}
          />
        </>
      ) : (
        <ModalContent>
          <WizardSection
            wizardConfiguration={{
              navigationType: null,
              questionStrategy: {
                type: "item",
                itemId,
              },
              requestInitialAnswers: "true",
              sourceDoc: {
                type: "quote",
                quoteId,
                contractorId: viewer.organisation.id,
              },
              showCloseButton: "true",
            }}
            initialAnswers={answers}
            onAnswer={async answers => {
              const mostRecentAnswer = answers[answers.length - 1];

              await applyQuoteItemActionsMutation({
                variables: {
                  input: {
                    applyItemActions: [
                      applyItemActionToApplyItemActionInput(
                        mostRecentAnswer.answer
                      ),
                    ],
                    projectId,
                    quoteId,
                  },
                },
              });
            }}
            onComplete={async result => {
              setIsCompleted(true);
              setAnswers(result.map(answer => answer.answer));
            }}
            onClose={handleCloseWithRefetch}
          />
        </ModalContent>
      )}
    </ModalDialog>
  );
}
